import { PANEL_SOURCE } from '../constants/panelSourceConstants'
import { findDefaultBranchWithPanelists } from './panelSourceUtils'

/**
 * Builds aggregation result object from branch hierarchy and panelist counts
 * @param {Array} branchHierarchy - Array of branch objects
 * @param {Object} panelistCounts - Object mapping branch_id to panelist count
 * @returns {Object} - Aggregation result with defaultBranchId, defaultPanelSource, panelistCounts
 */
export const buildAggregationResult = (branchHierarchy, panelistCounts) => {
  const defaultBranchId = findDefaultBranchWithPanelists(branchHierarchy, panelistCounts)
  const defaultSelectedBranchId = defaultBranchId || null

  return {
    defaultBranchId,
    defaultSelectedBranchId,
    defaultPanelSource: PANEL_SOURCE.OUR_PANEL,
    panelistCounts,
  }
}

/**
 * Builds panel source filters (selected and applied) from aggregation result and proximity settings
 * @param {Object} aggregationResult - Result from buildAggregationResult
 * @param {Object} proximitySettings - Proximity allocation settings
 * @param {boolean} proximitySettings.use_default_proximity_allocation - Whether to use default proximity
 * @param {number} proximitySettings.default_proximity_allocation - Default proximity distance
 * @param {Object} defaultSelectedFilters - Default selected filters object
 * @param {Object} defaultAppliedFilters - Default applied filters object
 * @returns {Object} - Object with selectedFilters and appliedFilters
 */
export const buildPanelSourceFilters = (
  aggregationResult,
  proximitySettings,
  defaultSelectedFilters,
  defaultAppliedFilters
) => {
  const { defaultPanelSource, defaultSelectedBranchId } = aggregationResult
  const { use_default_proximity_allocation, default_proximity_allocation } = proximitySettings

  const panelSourceFilter = {
    panelSource: defaultPanelSource,
    selectedBranchId: defaultSelectedBranchId,
  }

  const selectedFilters = {
    ...defaultSelectedFilters,
    radiusSearchSelected: use_default_proximity_allocation,
    radiusDistance: default_proximity_allocation.toString(),
    ...panelSourceFilter,
  }

  const appliedFilters = {
    ...defaultAppliedFilters,
    radiusSearchSelected: use_default_proximity_allocation,
    radiusDistance: default_proximity_allocation.toString(),
    geoFiltersApplied: use_default_proximity_allocation,
    ...panelSourceFilter,
  }

  return { selectedFilters, appliedFilters }
}

/**
 * Checks if fullSelectedLoan has just finished loading
 * @param {Object} prevProps - Previous props
 * @param {Object} currentProps - Current props
 * @returns {boolean} - True if fullSelectedLoan just loaded
 */
export const hasFullSelectedLoanJustLoaded = (prevProps, currentProps) => {
  const prevData = prevProps.fullSelectedLoan?.data
  const currentData = currentProps.fullSelectedLoan?.data
  const prevIsLoading = prevProps.fullSelectedLoan?.isLoading
  const currentIsLoading = currentProps.fullSelectedLoan?.isLoading

  const justLoaded = !prevData && currentData && !currentIsLoading
  const dataChanged = prevData !== currentData && currentData && !currentIsLoading
  const finishedLoading = prevIsLoading && !currentIsLoading && currentData

  return justLoaded || dataChanged || finishedLoading
}

/**
 * Determines if aggregation query should be executed based on props and state
 * @param {Object} params - Parameters object
 * @param {Object} params.flags - Feature flags
 * @param {boolean} params.aggregationQueryExecuted - Whether aggregation has been executed
 * @param {boolean} params.fullSelectedLoanJustLoaded - Whether fullSelectedLoan just loaded
 * @param {Object} params.fullSelectedLoanData - Full selected loan data
 * @returns {boolean} - True if aggregation should be executed
 */
export const shouldExecuteAggregation = ({
  flags,
  aggregationQueryExecuted,
  fullSelectedLoanJustLoaded,
  fullSelectedLoanData,
}) => {
  return Boolean(
    flags?.panelSourceHierarchy &&
    !aggregationQueryExecuted &&
    fullSelectedLoanJustLoaded &&
    fullSelectedLoanData?.branch_hierarchy
  )
}

/**
 * Checks if panel source filters need to be restored when switching tabs
 * @param {Object} aggregationResult - Stored aggregation result
 * @param {Object} currentFilters - Current selected filters
 * @returns {boolean} - True if filters need restoration
 */
export const shouldRestoreAggregationFilters = (aggregationResult, currentFilters) => {
  if (!aggregationResult) {
    return false
  }

  const { defaultPanelSource, defaultSelectedBranchId } = aggregationResult
  const { panelSource: currentPanelSource, selectedBranchId: currentSelectedBranchId } = currentFilters

  return currentPanelSource !== defaultPanelSource || currentSelectedBranchId !== defaultSelectedBranchId
}
